// @ts-strict-ignore
import moment from 'moment-timezone'
import ct, { TimezoneName, Timezone } from 'countries-and-timezones'

export type { TimezoneName } from 'countries-and-timezones'

const allTimezones = ct.getAllTimezones() as { [key in TimezoneName]: Timezone }
const allTimezoneNames = Object.keys(allTimezones) as TimezoneName[]

// For aliases, see: https://github.com/rails/rails/blob/master/activesupport/lib/active_support/values/time_zone.rb
export const rawTimezones = allTimezoneNames.map((tzAlias: TimezoneName) => ({
  alias: tzAlias,
  value: Math.trunc(allTimezones[tzAlias].utcOffset / 60),
  utcOffsetString: `UTC ${allTimezones[tzAlias].utcOffsetStr}`,
  dstOffsetString: `DST ${allTimezones[tzAlias].dstOffsetStr}`,
  minutes: allTimezones[tzAlias].utcOffset % 60,
  abbreviation: undefined as string, // TODO: ?
  name: allTimezones[tzAlias].name,
  extraText: allTimezones[tzAlias].aliasOf ? `(${allTimezones[tzAlias].aliasOf})` : undefined,
}))

export function generateTimezoneName(timezone: typeof rawTimezones[0], withExtraText = true) {
  return `${timezone.name}${timezone.extraText && withExtraText ? ` ${timezone.extraText}` : ''}`
}

/**
 * Set the given UTC offset or timezone name in the given timestamp, editing the resulting timestamp.
 *
 * This means if a 6 PM timestamp is passed and a timezone or offset that is 2 hours earlier is also passed,
 * the resulting timestamp will equal to 6 PM in the given timezone, so 8 PM for the local time.
 *
 * @param date the input timestamp
 * @param offset the timezone name or offset to set
 */
export function setUTCOffset(date: moment.MomentInput, offset: number | TimezoneName): moment.Moment {
  if (typeof offset === 'number') {
    return moment(date).utcOffset(offset, true)
  }

  if (!offset) return moment(date)

  return moment(date).tz(offset, true)
}

export const timezones = rawTimezones.map(timezone => ({
  ...timezone, title: generateTimezoneName(timezone, true),
}))

const MAX_NAME_LENGTH = 40

export const compactTimezones = (rawTimezones.reduce((list: any = [], current) => {
  if (list.length > 0) {
    const last = list[list.length - 1]
    if (last.value === current.value && last.minutes === current.minutes) {
      let alias = last.alias || []
      let abbreviation = last.abbreviation || current.abbreviation
      if (current.alias) alias.push(current.alias)
      let name = last.name
      if (name.length < MAX_NAME_LENGTH) {
        name += `, ${current.name}`
      } else if (!/\.\.\.$/.test(name)) {
        name += '...'
      }
      return list.slice(0, -1).concat({ ...last, name, alias, abbreviation })
    }
    return list.concat({ ...current, alias: [current.alias], abbreviation: current.abbreviation })
  }
  return [{ ...current, alias: [current.alias], abbreviation: current.abbreviation }]
}) as any).map(timezone => ({ ...timezone, title: generateTimezoneName(timezone, false) }))
  .sort((first, second) => first.value - second.value)

// NOTE: using `moment()` here without `new` makes this crash in Jest for some reason
export const defaultTimezone = parseInt((new (moment as any)()).format('Z') as string, 10)

export function initializeTimezones() {
  moment.tz.link(timezones.map(timezone => `${timezone.title}|${timezone.alias}`))
  moment.tz.link(compactTimezones.map(timezone => `${timezone.title}|${timezone.alias}`))
}

export function detectTimezoneName() {
  return moment.tz.guess() as TimezoneName
}

export function detectTimezoneOffset() {
  try {
    // see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset
    const negativeOffsetInMinutes = moment().toDate().getTimezoneOffset()
    return (-1 * negativeOffsetInMinutes) / 60
  } catch (error) {
    console.error(error)
  }
  return null
}

export function timezoneStringToValue(str) {
  if (typeof str !== 'string') return null
  if (str === 'GMT' || str === 'UTC') return 0
  const match = str.match(/[+-]\d+$/)
  if (match && match[0]) return parseInt(match[0], 10)
  return null
}

export function searchTimezoneByTitle(search: string) {
  if (!search) return null

  return timezones.find(tz => tz.title.toLocaleLowerCase().includes(search?.toLocaleLowerCase() || ''))
}

export function getTimezoneByAlias(search: string) {
  if (!search) return null

  return timezones.find(tz => tz.alias === search)
}

export function isPast(timestamp?: string | null) {
  if (!timestamp) return null

  const m = moment(timestamp)
  if (!m.isValid()) return null

  return m.isBefore(moment())
}
